<template>
	<div>
		<h2>{{ $t('tiers.menu_iban') }}</h2>

		<p>{{ $t('tiers.rib_status.explication') }}</p>

		<div class="box">
			<CustomTable
				ref="table"
				id_table="tiers_rib"
				:items="ribs"
				:busy.sync="table_busy"
				primaryKey="tiersrib_id"
			/>
		</div>

		<RibForm
			ref="rib_form"
			:tiers_id="parseInt(tiers_id)"
			@submit="reload"
		/>

		<b-modal ref="modal_delete" hide-footer>
			<template v-slot:modal-title>
				{{ $t('tiers.rib_form.supprimer_titre') }}
			</template>
			<p>{{ $t('tiers.rib_form.supprimer_confirmation') }}</p>
			<div class="row justify-content-around">
				<b-button class="mt-3 btn-block rounded-pill col-5" variant="primary" :disabled="processing" @click="doDelete">
					<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse />
					{{ $t('global.oui') }}
				</b-button>
				<b-button class="mt-3 btn-block rounded-pill col-5" variant="secondary" :disabled="processing" @click="cancelDelete">
					{{ $t('global.non') }}
				</b-button>
			</div>
		</b-modal>

		<b-modal ref="modal_default" hide-footer>
			<template v-slot:modal-title>
				{{ $t('tiers.rib_form.reference_titre') }}
			</template>
			<p>{{ $t('tiers.rib_form.reference_explication') }}</p>
			<b-form-radio-group v-model="default_choice" stacked>
				<b-form-radio v-for="rib in default_candidates" :key="rib.tiersrib_id" :value="rib.tiersrib_id">
					{{ rib.tiersrib_content }}
					<span v-if="rib.tiersrib_label">— {{ rib.tiersrib_label }}</span>
				</b-form-radio>
			</b-form-radio-group>
			<div class="text-center mt-4">
				<b-button variant="primary" class="btn-rounded" :disabled="processing || !default_choice" @click="doSetDefault">
					{{ $t('global.valider') }}
					<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse />
				</b-button>
			</div>
		</b-modal>

		<ActionOverlay ref="overlay" />
	</div>
</template>

<script type="text/javascript">
	import Tiers from "@/mixins/Tiers.js"
	import Tools from "GroomyRoot/mixins/Tools.js"
	import Navigation from "GroomyRoot/mixins/Navigation.js"

	export default {
		name: "TiersRibList",
		mixins: [Tiers, Tools, Navigation],
		props: ['tiers_id'],
		data() {
			return {
				ribs: [],
				table_busy: true,
				to_delete: null,
				default_candidates: [],
				default_choice: null,
				processing: false,
				events_tab: {
					'TableAction::goToAddTiersRib': () => {
						this.addOrEdit()
					},
					'TableAction::goToEditTiersRib': (rib) => {
						this.addOrEdit(rib)
					},
					'TableAction::goToApproveTiersRib': (ribs) => {
						this.setStatus(ribs, 'approuve')
					},
					'TableAction::goToQuarantineTiersRib': (ribs) => {
						this.setStatus(ribs, 'quarantaine')
					},
					'TableAction::goToDeleteTiersRib': (ribs) => {
						this.confirmDelete(ribs)
					}
				}
			}
		},
		mounted() {
			this.init_component()
		},
		methods: {
			/**
			 * Charge les coordonnées bancaires du tiers et traduit leur statut de contrôle.
			 */
			async init_component() {
				this.table_busy = true
				const res = await this.loadTiersRibs(this.tiers_id)
				// Le statut et la date d'approbation sont préparés ici : le tableau rend ses cellules
				// telles quelles, un code brut et une date ISO s'y afficheraient en l'état.
				this.ribs = res.map(rib => ({
					...rib,
					tiersrib_status_label: this.getTrad('tiers.rib_status.' + rib.tiersrib_status),
					tiersrib_source_label: this.getTrad('tiers.rib_source.' + rib.tiersrib_source),
					tiersrib_validated_at: this.formatDate(rib.tiersrib_validated_at, true)
				}))
				this.table_busy = false
			},
			reload() {
				if(this.$refs.table && typeof this.$refs.table.unselectAll === 'function') {
					this.$refs.table.unselectAll()
				}
				this.init_component()
			},
			addOrEdit(rib = null) {
				this.$refs.rib_form.open(rib)
			},
			/**
			 * Approuve ou remet en quarantaine les coordonnées sélectionnées.
			 *
			 * @param {Array} ribs
			 * @param {string} status
			 */
			async setStatus(ribs, status) {
				if(this.processing) {
					return
				}

				this.processing = true
				this.$refs.overlay.show(this.$t('global.enregistrement_en_cours'))

				let all_ok = true
				// Seule la dernière réponse compte : chaque bascule rend l'état des comptes vérifiés
				// tel qu'il est à cet instant, et c'est le dernier qui vaut.
				let candidates = []

				for(const rib of ribs) {
					const res = await this.setTiersRibStatus(this.tiers_id, rib.tiersrib_id, status)
					if(res === null) {
						all_ok = false
					}
					else {
						candidates = res
					}
				}

				if(all_ok) {
					await this.$refs.overlay.showSuccess(this.$t('global.enregistrement_succes'))
				}
				else {
					this.$refs.overlay.hide()
					this.failureToast('toast.info_modif_failed')
				}

				this.processing = false
				this.reload()

				if(candidates.length > 0) {
					this.askDefault(candidates)
				}
			},
			confirmDelete(ribs) {
				this.to_delete = ribs
				this.$refs.modal_delete.show()
			},
			cancelDelete() {
				this.to_delete = null
				this.$refs.modal_delete.hide()
			},
			/**
			 * Supprime les coordonnées sélectionnées après confirmation.
			 *
			 * @returns {Promise<void>}
			 */
			async doDelete() {
				if(this.processing || !this.to_delete || this.to_delete.length === 0) {
					return
				}

				this.processing = true
				this.$refs.overlay.show(this.$t('global.suppression_en_cours'))

				let all_ok = true
				// Seule la dernière réponse compte : chaque suppression rend l'état des comptes
				// vérifiés tel qu'il est à cet instant, et c'est le dernier qui vaut.
				let candidates = []

				for(const rib of this.to_delete) {
					const res = await this.deleteTiersRib(this.tiers_id, rib.tiersrib_id)
					if(res === null) {
						all_ok = false
					}
					else {
						candidates = res
					}
				}

				if(all_ok) {
					await this.$refs.overlay.showSuccess({
						text: this.$t('global.suppression_succes'),
						icon: ['far', 'trash-alt']
					})
				}
				else {
					this.$refs.overlay.hide()
					this.failureToast('toast.info_modif_failed')
				}

				this.processing = false
				this.$refs.modal_delete.hide()
				this.to_delete = null
				this.reload()

				if(candidates.length > 0) {
					this.askDefault(candidates)
				}
			},
			/**
			 * Demande sur quel compte vérifié partiront désormais les virements du fournisseur.
			 *
			 * @param {Array} candidates
			 */
			askDefault(candidates) {
				this.default_candidates = candidates
				this.default_choice = null
				this.$refs.modal_default.show()
			},
			/**
			 * Enregistre le compte de référence choisi.
			 *
			 * @returns {Promise<void>}
			 */
			async doSetDefault() {
				if(this.processing || !this.default_choice) {
					return
				}

				this.processing = true
				this.$refs.overlay.show(this.$t('global.enregistrement_en_cours'))

				const res = await this.setTiersRibDefault(this.tiers_id, this.default_choice)

				if(res) {
					await this.$refs.overlay.showSuccess(this.$t('global.enregistrement_succes'))
				}
				else {
					this.$refs.overlay.hide()
					this.failureToast('toast.info_modif_failed')
				}

				this.processing = false
				this.$refs.modal_default.hide()
				this.reload()
			}
		},
		components: {
			CustomTable: () => import('GroomyRoot/components/Table/CustomTable'),
			RibForm: () => import('@/components/Tiers/RibForm'),
			ActionOverlay: () => import('@/components/Utils/ActionOverlay')
		}
	}
</script>
